import { type FC, useMemo } from "react";

import {
  Home,
  Landmark,
  ChartNoAxesCombined,
  Ticket,
  CircleDollarSign,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { Link } from "react-router-dom";
import type { LucideIcon } from "lucide-react";

import { POST_LOSS_ROUTES } from "@/shared/constants/paths";
import { INVENTORY_CATEGORY_GROUPS } from "@/shared/constants/estateCategories";
import { usePerson } from "@/shared/hooks/useAppContext";
import { useEstateItems } from "@/shared/hooks/useEstateItems";
import { usePaymentTier } from "@/shared/hooks/usePaymentTier";
import { getGroupHex, DEBT_SEGMENT_HEX } from "@/shared/utils/categoryColors";
import { hasMinTier } from "@/shared/utils/tierOrder";
import { getEstateDonutData, sumByGroup } from "@/shared/utils/estateMath";
import { formatCurrency, formatCompactDollars } from "@/shared/utils/formatters";

interface CategoryConfig {
  label: string;
  icon: LucideIcon;
  iconBg: string;
  color: string;
  iconColor: string;
}

const CATEGORY_CONFIG: CategoryConfig[] = [
  {
    label: "Property",
    icon: Home,
    iconBg: "bg-purple-100",
    color: getGroupHex("Real Estate"),
    iconColor: getGroupHex("Real Estate"),
  },
  {
    label: "Financials",
    icon: Landmark,
    iconBg: "bg-blue-100",
    color: getGroupHex("Financial Accounts"),
    iconColor: getGroupHex("Financial Accounts"),
  },
  {
    label: "Insurance",
    icon: ChartNoAxesCombined,
    iconBg: "bg-rose-50",
    color: getGroupHex("Insurance"),
    iconColor: getGroupHex("Insurance"),
  },
  {
    label: "Debts",
    icon: Ticket,
    iconBg: "bg-red-50",
    color: DEBT_SEGMENT_HEX,
    iconColor: getGroupHex("Debts and Liabilities"),
  },
];

const GROUP_TILE_INDEX: Record<string, number> = {
  "Real Estate": 0,
  "Tangible Personal Property": 0,
  "Financial Accounts": 1,
  "Business Interests": 1,
  "Other Assets": 1,
  Insurance: 2,
  "Debts and Liabilities": 3,
  "Ongoing Estate Expenses": 3,
  "Other Debts": 3,
};

interface AssetsDebtsWidgetProps {
  personName: string;
  estateId?: number;
  hasAuthority?: boolean;
}

export const AssetsDebtsWidget: FC<AssetsDebtsWidgetProps> = ({
  personName,
  estateId: estateIdProp,
  hasAuthority,
}) => {
  const person = usePerson();
  const estateId = estateIdProp ?? person?.estate_id;
  const { assets, debts, isLoading, error } = useEstateItems(estateId);
  const { tier } = usePaymentTier(estateId);
  const isPremium = hasMinTier(tier, "premium");

  const allItems = useMemo(() => [...(assets ?? []), ...(debts ?? [])], [assets, debts]);

  const categoryTotals = useMemo(() => {
    const totals = CATEGORY_CONFIG.map(() => 0);
    const groupTotals = sumByGroup(allItems, INVENTORY_CATEGORY_GROUPS);
    for (const gt of groupTotals) {
      const idx = GROUP_TILE_INDEX[gt.group];
      if (idx !== undefined) {
        totals[idx] += gt.value;
      }
    }
    return totals;
  }, [allItems]);

  const grandTotal = allItems.reduce((sum, item) => sum + Math.abs(Number(item.balance)), 0);
  const { netValue } = useMemo(() => getEstateDonutData(allItems), [allItems]);
  const isEmpty = !isLoading && !error && grandTotal === 0;

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-base font-semibold tracking-[-0.02em]">{personName}'s estate record</p>
        <div className="flex items-center justify-center border border-primary rounded-xl bg-white p-6 h-[200px]">
          <Loader2 className="size-6 text-secondary animate-spin" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col gap-3">
        <p className="text-base font-semibold tracking-[-0.02em]">{personName}'s estate record</p>
        <div className="flex items-center justify-center border border-primary rounded-xl bg-white p-6 h-[200px]">
          <p className="text-sm text-secondary">
            Assets and debts couldn't load. Refresh the page to try again. Nothing you've added is
            lost.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="text-base font-semibold tracking-[-0.02em]">{personName}'s estate record</p>
      <div className="flex flex-col gap-8 border border-primary rounded-xl bg-white p-6">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <div className="flex items-start justify-between">
              <p className="text-base font-semibold tracking-[-0.02em]">Assets & debts</p>
              <Link
                to={POST_LOSS_ROUTES.ESTATE_INVENTORY}
                className="text-sm text-brand no-underline hover:underline tracking-[-0.02em]"
              >
                View all
              </Link>
            </div>
            <p className="text-sm text-secondary tracking-[-0.02em]">
              {isEmpty
                ? hasAuthority
                  ? "Run Elayne Asset Search to find what's here"
                  : "Nothing added yet. Start with what you know."
                : "What's been found and added so far"}
            </p>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-6">
            {isEmpty ? (
              <EmptyDonut />
            ) : (
              <FilledDonut totals={categoryTotals} grandTotal={grandTotal} netValue={netValue} />
            )}
            <div className="flex-1 grid grid-cols-2 gap-3 w-full min-w-0">
              {CATEGORY_CONFIG.map((cat, i) => (
                <CategoryTile
                  key={cat.label}
                  config={cat}
                  value={isEmpty ? undefined : categoryTotals[i]}
                  isEmpty={isEmpty}
                />
              ))}
            </div>
          </div>
        </div>
        {!isPremium && !hasAuthority && (
          <Link
            to={POST_LOSS_ROUTES.FIND_ASSETS}
            className="flex items-start gap-3 border border-blue-500 rounded-lg bg-blue-50 p-4 no-underline"
          >
            <div className="size-8 rounded border border-primary bg-white flex items-center justify-center shrink-0">
              <CircleDollarSign className="size-4 text-blue-500" />
            </div>
            <div className="flex flex-1 items-center gap-3 min-w-0">
              <div className="flex flex-col gap-2 flex-1 min-w-0">
                <p className="text-sm font-medium tracking-[-0.02em]">
                  Find what memory and mail can miss
                </p>
                <p className="text-sm text-secondary tracking-[-0.02em]">
                  Elayne Asset Search checks IRS records and 100+ other sources for accounts
                </p>
              </div>
              <ChevronRight className="size-5 text-secondary shrink-0" />
            </div>
          </Link>
        )}
      </div>
    </div>
  );
};

const EmptyDonut: FC = () => (
  <div className="relative size-[152px] shrink-0">
    <svg viewBox="0 0 152 152" className="size-full" aria-hidden="true">
      <circle cx="76" cy="76" r="58" fill="none" stroke="#d6d3d1" strokeWidth="20" />
      <circle
        cx="76"
        cy="76"
        r="58"
        fill="none"
        stroke="#e7e5e4"
        strokeWidth="20"
        strokeDasharray="91 273"
        strokeDashoffset="-45"
      />
      <circle
        cx="76"
        cy="76"
        r="58"
        fill="none"
        stroke="#c8c4c0"
        strokeWidth="20"
        strokeDasharray="91 273"
        strokeDashoffset="-182"
      />
      <circle
        cx="76"
        cy="76"
        r="58"
        fill="none"
        stroke="#f5f5f4"
        strokeWidth="20"
        strokeDasharray="55 309"
        strokeDashoffset="-273"
      />
    </svg>
    <div className="absolute inset-0 flex items-center justify-center">
      <p className="text-xs text-tertiary text-center leading-snug">
        Not yet
        <br />
        added
      </p>
    </div>
  </div>
);

const FilledDonut: FC<{ totals: number[]; grandTotal: number; netValue: number }> = ({
  totals,
  grandTotal,
  netValue,
}) => {
  const circumference = 2 * Math.PI * 58;
  let offset = 0;

  return (
    <div className="relative size-[152px] shrink-0">
      <svg viewBox="0 0 152 152" className="size-full -rotate-90" aria-hidden="true">
        {CATEGORY_CONFIG.map((cat, i) => {
          const pct = grandTotal > 0 ? totals[i] / grandTotal : 0;
          const dashLength = pct * circumference;
          const dashOffset = -offset;
          offset += dashLength;
          if (dashLength === 0) return null;
          const overlap = 1;
          return (
            <circle
              key={cat.label}
              cx="76"
              cy="76"
              r="58"
              fill="none"
              stroke={cat.color}
              strokeWidth="20"
              strokeDasharray={`${dashLength + overlap} ${circumference - dashLength - overlap}`}
              strokeDashoffset={dashOffset}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex items-center justify-center">
        <p className="text-sm font-semibold text-center">{formatCompactDollars(netValue)}</p>
      </div>
    </div>
  );
};

const CategoryTile: FC<{
  config: CategoryConfig;
  value?: number;
  isEmpty: boolean;
}> = ({ config, value, isEmpty }) => (
  <div className="flex flex-1 items-center gap-3 border border-primary rounded-lg bg-white p-3 min-w-0">
    <div className={`${isEmpty ? "bg-subtle" : config.iconBg} rounded p-2 shrink-0`}>
      <config.icon
        className={`size-4 ${isEmpty ? "text-secondary" : ""}`}
        style={isEmpty ? undefined : { color: config.iconColor }}
      />
    </div>
    <div className="flex flex-col min-w-0">
      <span
        className={`text-sm tracking-[-0.02em] truncate ${isEmpty ? "text-secondary" : "font-medium"}`}
      >
        {config.label}
      </span>
      {value !== undefined && (
        <span className="text-sm text-secondary tracking-[-0.02em]">{formatCurrency(value)}</span>
      )}
    </div>
  </div>
);
